import { useState, useRef, useEffect } from 'react';
import { chatAPI } from '../services/api';

export const useChatMessages = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Character limit constants
  const MAX_CHARACTERS = 1000;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle input text change with character limit
  const handleInputChange = (e) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARACTERS) {
      setInputText(newText);
    }
  };

  // Add initial welcome message
  const addInitialMessage = (greeting) => {
    const welcomeMessage = {
      id: 1,
      text: greeting,
      isAI: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([welcomeMessage]);
  };

  // Send message with SSE streaming
  const sendMessage = async (sessionId, userToken, updateActivity, updateUserInfo, updateRateLimits, setError, onNameCaptured) => {
    if (!inputText.trim() || isTyping || !sessionId || !userToken) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      isAI: false,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    const aiMessageId = Date.now() + 1;
    const aiMessageTimestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);
    setError(null);
    updateActivity();

    // Add placeholder AI message that will be filled in via streaming
    setMessages(prev => [...prev, { id: aiMessageId, text: '', isAI: true, timestamp: aiMessageTimestamp, isStreaming: true }]);

    try {
      await chatAPI.sendMessageStream(inputText, sessionId, userToken, (event) => {
        if (event.type === 'content') {
          setMessages(prev => prev.map(msg =>
            msg.id === aiMessageId ? { ...msg, text: msg.text + event.text } : msg
          ));
        } else if (event.type === 'done') {
          setMessages(prev => prev.map(msg =>
            msg.id === aiMessageId ? { ...msg, isStreaming: false } : msg
          ));
          if (event.userInfo) updateUserInfo(event.userInfo, onNameCaptured);
          updateRateLimits(event.rateLimits);
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
      });
    } catch (error) {
      console.error('Failed to send message:', error);

      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id && msg.id !== aiMessageId));

      if (error.message.includes('Session ID is required')) {
        setError('Session expired. Please refresh the page.');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('lastActivity');
      } else {
        setError(error.message);
      }
    } finally {
      setIsTyping(false);
    }
  };

  // Handle key press for sending messages
  const handleKeyPress = (e, sendCallback) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendCallback();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (question) => {
    setInputText(question);
    inputRef.current?.focus();
  };

  // Check if user can send message
  const canSendMessage = (sessionId, userToken, rateLimits) => {
    return inputText.trim() && !isTyping && sessionId && userToken && rateLimits.dailyRemaining > 0;
  };

  // Get character count info
  const getCharacterInfo = () => {
    const characterCount = inputText.length;
    const isNearLimit = characterCount > MAX_CHARACTERS * 0.8; // 80% of limit
    const isAtLimit = characterCount >= MAX_CHARACTERS;
    
    return {
      characterCount,
      isNearLimit,
      isAtLimit,
      maxCharacters: MAX_CHARACTERS
    };
  };

  return {
    // State
    messages,
    inputText,
    isTyping,
    messagesEndRef,
    inputRef,
    
    // Actions
    handleInputChange,
    addInitialMessage,
    sendMessage,
    handleKeyPress,
    handleSuggestionClick,
    canSendMessage,
    getCharacterInfo,
    setMessages
  };
}; 